import React, { useState, useEffect, useContext } from "react";
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
} from "@mui/material";
import { Edit, Delete, Add, LockOpen } from "@mui/icons-material";
import Header from "../components/Layout/Header";
import { usersAPI } from "../services/api";
import { AuthContext } from "../context/AuthContext";

const UserManagementPage = () => {
  const { user } = useContext(AuthContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    role: "Standard User",
  });

  useEffect(() => {
    if (user?.role === "Super User") {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const response = await usersAPI.getAll();
      setUsers(response.data);
    } catch (err) {
      setError("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (userToEdit = null) => {
    if (userToEdit) {
      setEditingUser(userToEdit);
      setFormData({
        username: userToEdit.username,
        email: userToEdit.email,
        password: "",
        role: userToEdit.role,
      });
    } else {
      setEditingUser(null);
      setFormData({
        username: "",
        email: "",
        password: "",
        role: "Standard User",
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingUser(null);
    setFormData({
      username: "",
      email: "",
      password: "",
      role: "Standard User",
    });
  };

  const handleSubmit = async () => {
    setError("");
    setSuccess("");

    try {
      if (editingUser) {
        await usersAPI.update(editingUser.id, formData);
        setSuccess("User updated successfully");
      } else {
        await usersAPI.create(formData);
        setSuccess("User created successfully");
      }
      handleCloseDialog();
      loadUsers();
    } catch (err) {
      setError(err.response?.data?.error || "Operation failed");
    }
  };

  const handleDelete = async (userId) => {
    if (window.confirm("Are you sure you want to delete this user?")) {
      try {
        await usersAPI.delete(userId);
        setSuccess("User deleted successfully");
        loadUsers();
      } catch (err) {
        setError(err.response?.data?.error || "Failed to delete user");
      }
    }
  };

  const handleUnlock = async (userId, username) => {
    if (window.confirm(`Are you sure you want to unlock the account for ${username}?`)) {
      try {
        await usersAPI.unlock(userId);
        setSuccess("User account unlocked successfully");
        loadUsers();
      } catch (err) {
        setError(err.response?.data?.error || "Failed to unlock user");
      }
    }
  };

  const isAccountLocked = (user) => {
    if (!user.locked_until) return false;
    const lockedUntil = new Date(user.locked_until);
    return lockedUntil > new Date();
  };

  const formatLockedUntil = (lockedUntil) => {
    if (!lockedUntil) return null;
    const date = new Date(lockedUntil);
    if (date <= new Date()) return null;
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (user?.role !== "Super User") {
    return (
      <Box>
        <Header />
        <Container maxWidth="lg" sx={{ mt: 4 }}>
          <Alert severity="error">
            You do not have permission to access this page.
          </Alert>
        </Container>
      </Box>
    );
  }

  return (
    <Box>
      <Header />
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={3}
        >
          <Typography variant="h4" component="h1">
            User Management
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            Add User
          </Button>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Username</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                const locked = isAccountLocked(u);
                const lockedUntil = formatLockedUntil(u.locked_until);
                return (
                  <TableRow key={u.id}>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>{u.role}</TableCell>
                    <TableCell>
                      {locked ? (
                        <Box>
                          <Chip
                            label="Locked"
                            color="error"
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          {lockedUntil && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Until: {lockedUntil}
                            </Typography>
                          )}
                          {u.failed_login_attempts > 0 && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Failed attempts: {u.failed_login_attempts}
                            </Typography>
                          )}
                        </Box>
                      ) : u.failed_login_attempts > 0 ? (
                        <Box>
                          <Chip
                            label="Active"
                            color="success"
                            size="small"
                            sx={{ mb: 0.5 }}
                          />
                          <Typography variant="caption" color="text.secondary" display="block">
                            Failed attempts: {u.failed_login_attempts}
                          </Typography>
                        </Box>
                      ) : (
                        <Chip label="Active" color="success" size="small" />
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(u.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(u)}
                        title="Edit user"
                      >
                        <Edit />
                      </IconButton>
                      {locked && (
                        <IconButton
                          size="small"
                          onClick={() => handleUnlock(u.id, u.username)}
                          title="Unlock account"
                          color="primary"
                        >
                          <LockOpen />
                        </IconButton>
                      )}
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(u.id)}
                        disabled={u.id === user.id}
                        title="Delete user"
                      >
                        <Delete />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          open={dialogOpen}
          onClose={handleCloseDialog}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>{editingUser ? "Edit User" : "Create User"}</DialogTitle>
          <DialogContent>
            <TextField
              fullWidth
              label="Username"
              margin="normal"
              value={formData.username}
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label="Email"
              type="email"
              margin="normal"
              value={formData.email}
              onChange={(e) =>
                setFormData({ ...formData, email: e.target.value })
              }
              required
            />
            <TextField
              fullWidth
              label={
                editingUser
                  ? "New Password (leave blank to keep current)"
                  : "Password"
              }
              type="password"
              margin="normal"
              value={formData.password}
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required={!editingUser}
            />
            <FormControl fullWidth margin="normal">
              <InputLabel>Role</InputLabel>
              <Select
                value={formData.role}
                onChange={(e) =>
                  setFormData({ ...formData, role: e.target.value })
                }
                label="Role"
              >
                <MenuItem value="Super User">Super User</MenuItem>
                <MenuItem value="Power User">Power User</MenuItem>
                <MenuItem value="Standard User">Standard User</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button onClick={handleSubmit} variant="contained">
              {editingUser ? "Update" : "Create"}
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserManagementPage;
